import Page from '../../layouts/main';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { useSelector } from 'react-redux';
import React from 'react';
import {
  NewsPostSubCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import {
  getLandingPageType,
  LandingPageTypes,
} from '../../common/helpers/domain/landingPages';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { generatePostSubcategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import MainTitle from '../../components/mainTitle/MainTitle';
import homeResource from '../../locales/th/home.json';
import Row from '../../components/rows/Row';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { DynamicComponentForArticleFeedModule } from '../../components/pages/homePage/DynamicComponents';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import config from '../../common/config';
import { parseNewsPostSubCategory } from '../../common/helpers/routing/landingPageUrlParser';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import { updatePageType } from '../../store/home/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import { getNoTabDefinition } from '../../components/articleFeed/articleFeedTab';
import logger from '../../common/helpers/logger/logger';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import {
  getAdSectByPageType,
  getAdSubsectByPostCategory,
} from '../../common/helpers/ads/AdsHelper';
import { WholeState } from '../../store/common/type';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackHeroArticle,
  trackMostPopularArticle,
} from '../../common/analytics/analyticsTracker';

interface NewSubLandingPageProps {
  postCategory: NewsPostSubCategory;
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
}

function NewsSubLandingPage({
  postCategory,
  canonicalLink,
  heroArticle,
  popularArticles,
}: NewSubLandingPageProps) {
  const landingPageType = getLandingPageType([postCategory], null);
  const analyticsSource = generateEventSourceByPageType(landingPageType);
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader pageType={landingPageType} canonicalLink={canonicalLink} />
      <Breadcrumb items={generatePostSubcategoryBreadcrumbList(postCategory)} />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title[postCategory]}
          subtitle={homeResource.subtitle[postCategory]}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(post) =>
                trackMostPopularArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(LandingPageTypes.NewsPage)}
          subsect={getAdSubsectByPostCategory(postCategory)}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getNoTabDefinition(null, [postCategory])}
          excludePostIds={[]}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(LandingPageTypes.NewsPage)}
          adSubsect={getAdSubsectByPostCategory(postCategory)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

NewsSubLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
  query,
}: CustomNextPageContext): Promise<NewSubLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  const postCategory = parseNewsPostSubCategory(query.category as string) as
    | PostSubcategory.PropertyNews
    | PostSubcategory.GeneralNews;
  try {
    const { heroArticle, mostPopular } = await fetchLandingPageTopContentData(
      apolloClient,
      {
        postCategories: [postCategory],
      },
    );

    const landingPageType = getLandingPageType([postCategory]);
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    return {
      postCategory,
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
    };
  } catch (e) {
    logger.logError('Error when fetching post for news sub-landing page:', e);
  }
};

export default NewsSubLandingPage;
